import React from 'react';
import {
  MobileTimePicker as MUIMobileTimePicker,
  MobileTimePickerProps,
} from '@mui/x-date-pickers/MobileTimePicker';

import TextField, { TextFieldProps } from '@mui/material/TextField';
import { alpha, styled } from '@mui/material/styles';
import { shouldForwardProp } from '../../util/shouldForwardProps';
import type { Moment } from 'moment';

// StyledTextField for Input-like appearance
const StyledTextField = styled(TextField, {
  shouldForwardProp,
})<TextFieldProps & { ownerState?: any }>(({ theme, ...props }) => {
  return {
    '& .MuiInputBase-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '3px solid transparent',
      fontWeight: 800,
      fontFamily: 'inherit',
      fontSize: '1rem',
      lineHeight: 1,
      padding: '6px',
      borderRadius: '12px',
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[200]
          : theme.palette.grey[900],
      background: 'transparent',
      marginTop: theme.palette.mode === 'dark' ? '6px' : '5px',
      height: theme.palette.mode === 'dark' ? '39px' : '37px',
      borderWidth: theme.palette.mode === 'dark' ? '3px' : '2px',
      borderColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[200] : '#C6C6C6',
      '& input': {
        padding: 0,
      },
      '& input::placeholder': {
        color:
          theme.palette.mode === 'dark' ? theme.palette.grey[600] : '#C6C6C6',
      },

      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
        {
          WebkitBoxShadow: `0 0 0 30px ${
            theme.palette.mode === 'dark'
              ? theme.palette.grey[200]
              : 'rgb(238, 238, 238)'
          } inset !important`,
          WebkitTextFillColor: `${theme.palette.grey[900]} !important`,
        },
      '&:hover, &:focus': {
        borderColor: alpha(
          theme.palette.primary.main,
          1 - theme.palette.action.hoverOpacity
        ),
      },
      '.MuiOutlinedInput-notchedOutline': {
        border: '3px solid transparent',
        borderWidth: theme.palette.mode === 'dark' ? '3px' : '2px',
        borderStyle: 'solid',
        borderColor: 'transparent',
      },

      ...(!props.ownerState?.isPickerValueEmpty &&
        theme.palette.mode === 'dark' && {
          background: theme.palette.grey[200],
          color: theme.palette.grey[900],
          '& svg': {
            color: theme.palette.grey[900],
          },
        }),

      ...(props.disabled && {
        borderColor: '#9E9E9E!important',
        color: theme.palette.grey[300],
        WebkitTextFillColor: theme.palette.grey[300],
        background: '#9E9E9E',
      }),

      ...(props.error && {
        borderColor: theme.palette.error.main,
      }),

      ...(props.fullWidth && {
        width: '100%',
      }),
    },
  };
});

interface TimerPickerProps extends Omit<MobileTimePickerProps<any>, 'slots'> {
  InputProps?: any;
  value?: Moment | null;
  onChange?: (value: Moment | null) => void;
}

const TimerPicker = (props: TimerPickerProps) => {
  const { InputProps, slotProps, ...otherProps } = props;

  return (
    <MUIMobileTimePicker
      {...otherProps}
      slots={{
        textField: StyledTextField,
      }}
      enableAccessibleFieldDOMStructure={false}
      slotProps={{
        ...slotProps,
        ...InputProps,
        textField: {
          ...slotProps?.textField,
          error: InputProps?.error,
        },
      }}
    />
  );
};

export default TimerPicker;
